import React, { useState } from 'react';
import ProModeOnboarding from './ProModeOnboarding';

/**
 * Pro Mode selector for the Fight to be King variant.
 * Renders:
 *  - Symmetric Classic / Pro 1 / Pro 2 buttons
 *  - Asymmetric toggle revealing per-side (White / Black) Pro selectors
 *
 * Self-contained UI for NewGamePanel — keeps NewGamePanel focused on overall game setup.
 */
const PRO_OPTIONS = [
  { id: 'off', icon: '⚔️', label: 'Classic', title: 'Classic Fight to be King (Duke keeps all captures)', testid: 'ftbk-pro-off' },
  { id: 'pro_1', icon: '👑', label: 'Pro 1 — Any move', title: 'Pro 1: Any move to baseline reclaims the throne (captures allowed)', testid: 'ftbk-pro-1' },
  { id: 'pro_2', icon: '🛡️', label: 'Pro 2 — Non-capture only', title: "Pro 2: Only non-capturing moves to baseline reclaim the throne. Duke also cannot touch opponent's baseline.", testid: 'ftbk-pro-2' }
];

const SIDE_OPTIONS = [
  { id: 'off', icon: '⚔️', label: 'Classic' },
  { id: 'pro_1', icon: '👑', label: 'Pro 1' },
  { id: 'pro_2', icon: '🛡️', label: 'Pro 2' }
];

const ProModeSelector = ({
  value,              // 'off' | 'pro_1' | 'pro_2'
  onChange,           // (newValue) => void
  asymmetric,         // boolean
  onAsymmetricChange, // (bool) => void
  whitePro,           // 'off' | 'pro_1' | 'pro_2'
  blackPro,           // 'off' | 'pro_1' | 'pro_2'
  onWhiteProChange,
  onBlackProChange
}) => {
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div className="setting-section" data-testid="ftbk-pro-toggle-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h3 style={{ margin: 0 }}>Professional Mode</h3>
        <button
          type="button"
          onClick={() => setShowHelp(true)}
          className="pro-help-icon"
          title="What is Pro Mode?"
          aria-label="Open Pro Mode help"
          data-testid="ftbk-pro-help-btn"
          style={{
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            border: '1px solid rgba(255, 138, 30, 0.6)',
            background: 'rgba(255, 138, 30, 0.12)',
            color: '#ff8a1e',
            cursor: 'pointer',
            fontSize: '0.8rem',
            fontWeight: 700,
            lineHeight: 1,
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >?</button>
      </div>
      <p className="embodiment-note">
        When the King is captured, the Duke loses its orthogonal (up/down/left/right) captures — only diagonals remain. Reach your own baseline rank to reclaim the throne.
        <br />
        <strong>Pro 2</strong> adds: the restricted Duke <em>cannot</em> step onto the opponent's baseline rank.
      </p>

      <div className="embodiment-icons" style={{ opacity: asymmetric ? 0.45 : 1 }}>
        {PRO_OPTIONS.map(opt => (
          <button
            key={opt.id}
            className={`embodiment-icon-button ${!asymmetric && value === opt.id ? 'active' : ''}`}
            onClick={() => { onAsymmetricChange(false); onChange(opt.id); }}
            title={opt.title}
            data-testid={opt.testid}
            disabled={asymmetric}
          >
            <span className="icon">{opt.icon}</span>
            <span className="label">{opt.label}</span>
          </button>
        ))}
      </div>

      <label className="checkbox-label" style={{ marginTop: '10px' }}>
        <input
          type="checkbox"
          checked={asymmetric}
          onChange={(e) => onAsymmetricChange(e.target.checked)}
          data-testid="ftbk-asymmetric-toggle"
        />
        <span>Use different Pro mode for each side (Asymmetric)</span>
      </label>

      {asymmetric && (
        <div data-testid="ftbk-asymmetric-section">
          <div className="setting-section" style={{ marginTop: '8px' }}>
            <h3>⚪ White Pro Mode</h3>
            <div className="embodiment-icons">
              {SIDE_OPTIONS.map(opt => (
                <button
                  key={`white-${opt.id}`}
                  className={`embodiment-icon-button ${whitePro === opt.id ? 'active' : ''}`}
                  onClick={() => onWhiteProChange(opt.id)}
                  data-testid={`ftbk-white-${opt.id}`}
                >
                  <span className="icon">{opt.icon}</span>
                  <span className="label">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="setting-section">
            <h3>⚫ Black Pro Mode</h3>
            <div className="embodiment-icons">
              {SIDE_OPTIONS.map(opt => (
                <button
                  key={`black-${opt.id}`}
                  className={`embodiment-icon-button ${blackPro === opt.id ? 'active' : ''}`}
                  onClick={() => onBlackProChange(opt.id)}
                  data-testid={`ftbk-black-${opt.id}`}
                >
                  <span className="icon">{opt.icon}</span>
                  <span className="label">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Re-openable onboarding modal for players who want to re-read the rules */}
      {showHelp && (
        <ProModeOnboarding forceOpen onClose={() => setShowHelp(false)} />
      )}
    </div>
  );
};

export default ProModeSelector;
